import { useState, useRef, useEffect } from 'react';
import { Send, MessageCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ChatInputProps } from '@/types/componentsTypes';
import { useLanguage } from '@/contexts/LanguageContext';

export function ChatInput({ onSendMessage, disabled = false }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  // Auto-focus input after sending message or when disabled state changes
  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-2"
    >
      <div className="relative flex-1">
        {/* Left icon inside input */}
        <MessageCircle className="absolute left-3 text-gray-400 pointer-events-none z-10" size={18} style={{ top: '47%', transform: 'translateY(-50%)' }} />

        {/* Input field with left padding */}
        <Input
          ref={inputRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t("chatPlaceholder")}
          disabled={disabled}
          className="pl-10 pr-12 py-2.5 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
        />

        {/* Orange send icon inside input (right) */}
        <button
          data-testid="chat-input-submit-button"
          type="submit"
          disabled={!message.trim() || disabled}
          className="absolute right-1 top-1/2 -translate-y-1/2 bg-brand-accent hover:bg-brand-accent/80 text-white rounded-full p-2 transition-colors disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
